import { useState, type FormEvent } from "react";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { validateRedirect } from "@njbsoft/utils";
import { customerApiClient } from "@/lib/auth";
import { type CustomerSessionTokens } from "@/lib/customer-session";
import { forgetPortal, readRememberedPortal } from "@/lib/portal-memory";
import { completeSignIn } from "@/lib/sign-in";
import { useCustomerStore } from "@/store";
import { useToast } from "@/hooks/use-toast";
import { useTurnstileGate } from "@/hooks/use-turnstile-gate";
import { MfaChallenge } from "@/components/mfa-challenge";
import { TurnstileWidget } from "@/components/turnstile-widget";
import { Button, buttonClasses, FormField, inputClass } from "@/components/ui";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface SignInSearch {
  redirect?: string;
}

export const Route = createFileRoute("/portal/$slug/")({
  validateSearch: (search: Record<string, unknown>): SignInSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  // A signed-in visitor goes on without switching accounts: the switcher is the
  // way to change accounts. A redirect the sign-in would have followed is
  // followed here too, so the guards re-run by a completed sign-in land on its
  // target directly rather than passing through Home.
  beforeLoad: ({ context, search }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: validateRedirect(search.redirect) ?? "/home" });
    }
  },
  component: SignInPage,
});

function SignInPage() {
  const { slug } = Route.useParams();
  const { redirect: redirectTo } = Route.useSearch();
  const { t } = useTranslation(["customer", "common"]);
  const navigate = useNavigate();
  const setMfaChallenge = useCustomerStore((s) => s.setMfaChallenge);
  const clearMfaChallenge = useCustomerStore((s) => s.clearMfaChallenge);
  const mfaPending = useCustomerStore((s) => s.mfaPending);
  const { apiError: showApiError } = useToast();
  const gate = useTurnstileGate();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Covers the windows isPending does NOT: gate.freshToken() resets the widget
  // and waits on the next solve before any mutation starts, and a sign-in
  // finished through the challenge panel completes with no mutation pending.
  // A second click in either would burn a second challenge or a second login.
  const [submitting, setSubmitting] = useState(false);
  const remembersThisUtility = readRememberedPortal()?.slug === slug;

  const finishSignIn = async (tokens: CustomerSessionTokens) => {
    setSubmitting(true);
    try {
      await completeSignIn(tokens, slug);
      void navigate({ to: validateRedirect(redirectTo) ?? "/home" });
    } finally {
      setSubmitting(false);
    }
  };

  const loginMutation = useMutation({
    mutationFn: async (creds: { email: string; password: string; turnstile_token?: string }) => {
      const { data, error } = await customerApiClient.POST("/auth/login", { body: creds });
      if (error) throw error;
      return data;
    },
    onSuccess: async (data) => {
      if (data.mfa_required && data.mfa_session_token && data.mfa_methods) {
        setMfaChallenge(data.mfa_session_token, data.mfa_methods);
        return;
      }
      if (data.access_token && data.refresh_token) {
        await finishSignIn({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_at: data.expires_at ?? "",
        });
      }
    },
    onError: (err: unknown) => showApiError((err as { code?: string })?.code),
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    const formData = new FormData(e.currentTarget);
    const email = String(formData.get("email") ?? "").trim();
    const password = String(formData.get("password") ?? "");

    const errors: Record<string, string> = {};
    if (!email) errors.email = t("common:validation.required");
    else if (!EMAIL_RE.test(email)) errors.email = t("common:validation.invalidEmail");
    if (!password) errors.password = t("common:validation.required");
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    // Every response is single-use. Reading the mount solve off the form
    // replays a spent token on the second attempt, which siteverify answers
    // `timeout-or-duplicate`, so a mistyped password would leave the form
    // failing the challenge until a reload.
    void gate
      .freshToken()
      .then((turnstileToken) => {
        loginMutation.mutate({
          email,
          password,
          // A challenge we do not have is a field we do not send: with no site
          // key the widget never mounts, and "" would claim an empty response
          // instead of no response.
          ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
        });
      })
      .finally(() => setSubmitting(false));
  };

  const forgetThisUtility = () => {
    forgetPortal();
    void navigate({ to: "/login" });
  };

  if (mfaPending) {
    return (
      <MfaChallenge
        onSuccess={(tokens) => void finishSignIn(tokens)}
        onCancel={() => clearMfaChallenge()}
      />
    );
  }

  const busy = submitting || loginMutation.isPending;

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-semibold tracking-[-0.01em] text-ink">
        {t("portal.signIn.title")}
      </h2>
      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        <FormField label={t("portal.signIn.email")} error={fieldErrors.email}>
          <input name="email" type="email" autoComplete="email" className={inputClass} />
        </FormField>
        <FormField label={t("portal.signIn.password")} error={fieldErrors.password}>
          <input
            name="password"
            type="password"
            autoComplete="current-password"
            className={inputClass}
          />
        </FormField>
        {/* The server skips verification when its secret is empty (dev), so
            an unset site key renders nothing at all. */}
        {gate.enabled && (
          <TurnstileWidget siteKey={gate.siteKey} onToken={gate.onToken} ref={gate.widgetRef} />
        )}
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? t("portal.signIn.submitting") : t("portal.signIn.submit")}
        </Button>
      </form>
      <Link
        to="/portal/$slug/forgot-password"
        params={{ slug }}
        className={buttonClasses("link", "sm")}
      >
        {t("portal.signIn.forgot")}
      </Link>
      <p className="text-sm text-muted">{t("portal.signIn.firstTime")}</p>
      {remembersThisUtility && (
        <Button variant="link" size="sm" onClick={forgetThisUtility}>
          {t("portal.signIn.forget")}
        </Button>
      )}
    </div>
  );
}
